import React from 'react';
import SEOHelmet from '../components/SEOHelmet';

const GrievancePolicyPage = () => {
  const lastUpdated = "October 29, 2025";

  return (
    <>
      <SEOHelmet
        title="Grievance Policy - Ceremonzy"
        description="Learn how to report content violations and file complaints on Ceremonzy. Our grievance redressal mechanism ensures quick resolution of issues."
        keywords="grievance policy, complaint process, content removal, takedown policy, ceremonzy support"
        url="/grievance-policy"
      />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Grievance Redressal Policy</h1>
            <p className="text-gray-600 mb-8">Last updated: {lastUpdated}</p>

            <div className="prose prose-lg max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-4">
                  This Grievance Redressal Policy outlines the mechanism for users to report content violations, 
                  file complaints, and seek resolution of issues on Ceremonzy.com. This policy is established 
                  in compliance with the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) 
                  Rules, 2021 and Section 79 of the Information Technology Act, 2000.
                </p>
                <div className="bg-green-50 border-l-4 border-green-400 p-4">
                  <p className="text-green-800">
                    <strong>Our Commitment:</strong> We are committed to providing a safe, secure, and lawful 
                    platform for all users and take all legitimate grievances seriously.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Types of Grievances</h2>
                <p className="text-gray-700 mb-4">You can file grievances for the following issues:</p>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">2.1 Content-Related Issues</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Inappropriate, offensive, or abusive content</li>
                  <li>Fake or misleading reviews and ratings</li>
                  <li>Defamatory content against individuals or businesses</li>
                  <li>Copyright or intellectual property violations</li>
                  <li>Privacy violations or unauthorized personal information sharing</li>
                  <li>Spam or promotional content in reviews</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">2.2 User Conduct Issues</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Harassment or bullying by other users</li>
                  <li>Impersonation of individuals or businesses</li>
                  <li>Fraudulent activities or scams</li>
                  <li>Violation of Terms of Use</li>
                  <li>Unauthorized access or hacking attempts</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">2.3 Platform Issues</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Technical problems affecting user experience</li>
                  <li>Account access or security issues</li>
                  <li>Payment or billing disputes</li>
                  <li>Data privacy concerns</li>
                  <li>Service quality complaints</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How to File a Grievance</h2>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">3.1 Online Complaint Form</h3>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
                  <p className="text-blue-800 mb-2">
                    <strong>Preferred Method:</strong> Use our online grievance form for fastest processing
                  </p>
                  <a href="#complaint-form" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors inline-block">
                    File Online Complaint
                  </a>
                </div>

                <h3 className="text-xl font-medium text-gray-800 mb-4">3.2 Email Submission</h3>
                <p className="text-gray-700 mb-3">                  Send your grievance to <strong>ceremonzy07@gmail.com</strong> with the following details:</p>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">3.3 Required Information</h3>
                <p className="text-gray-700 mb-3">Please include the following details in your complaint:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Your Details:</strong> Name, email, phone number</li>
                  <li><strong>Account Information:</strong> Your registered username/email</li>
                  <li><strong>Issue Type:</strong> Category of grievance</li>
                  <li><strong>Detailed Description:</strong> Clear explanation of the issue</li>
                  <li><strong>Evidence:</strong> Screenshots, URLs, or other supporting material</li>
                  <li><strong>Content Location:</strong> Specific page URLs or vendor profiles</li>
                  <li><strong>Preferred Resolution:</strong> What action you expect us to take</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Grievance Processing Timeline</h2>
                
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border">
                  <h3 className="text-xl font-medium text-gray-800 mb-4">Processing Steps & Timeline</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">1</div>
                      <div>
                        <h4 className="font-medium text-gray-800">Acknowledgment</h4>
                        <p className="text-gray-600 text-sm">Within 24 hours of receiving your complaint</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">2</div>
                      <div>
                        <h4 className="font-medium text-gray-800">Initial Review</h4>
                        <p className="text-gray-600 text-sm">Within 48 hours - Assessment and categorization</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">3</div>
                      <div>
                        <h4 className="font-medium text-gray-800">Investigation</h4>
                        <p className="text-gray-600 text-sm">Within 7 days - Detailed investigation of the issue</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">4</div>
                      <div>
                        <h4 className="font-medium text-gray-800">Resolution</h4>
                        <p className="text-gray-600 text-sm">Within 15 days - Final decision and action taken</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Content Removal Process</h2>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">5.1 Emergency Takedown</h3>
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                  <p className="text-red-800">
                    <strong>Urgent Cases:</strong> Content involving illegal activities, threats, or court orders 
                    will be removed within 36 hours of notification.
                  </p>
                </div>

                <h3 className="text-xl font-medium text-gray-800 mb-3">5.2 Standard Content Review</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Content violating community guidelines: 3-7 days</li>
                  <li>Disputed reviews or ratings: 5-10 days (includes vendor response)</li>
                  <li>Copyright claims: 7-14 days (includes counter-notice period)</li>
                  <li>Privacy violation claims: 3-5 days</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">5.3 Actions We May Take</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Remove or modify the reported content</li>
                  <li>Issue warnings to users</li>
                  <li>Temporarily suspend user accounts</li>
                  <li>Permanently ban users for severe violations</li>
                  <li>Report illegal activities to law enforcement</li>
                  <li>Provide clarifications if no violation is found</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Appeal Process</h2>
                <p className="text-gray-700 mb-4">
                  If you disagree with our decision, you can file an appeal:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Submit appeal within 30 days of our decision</li>
                  <li>Provide additional evidence or clarification</li>
                  <li>Appeals are reviewed by a different team member</li>
                  <li>Appeal decisions are final and binding</li>
                  <li>Appeal response time: 7-15 business days</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. False or Malicious Complaints</h2>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                  <p className="text-yellow-800">
                    <strong>Warning:</strong> Filing false, frivolous, or malicious complaints may result in 
                    action against your account, including temporary or permanent suspension.
                  </p>
                </div>
                <p className="text-gray-700 mb-4">We reserve the right to:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Reject complaints that are clearly false or made in bad faith</li>
                  <li>Take action against users who abuse the grievance system</li>
                  <li>Require additional verification for repeated complaints</li>
                  <li>Report persistent abuse to relevant authorities</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Legal Compliance</h2>
                <p className="text-gray-700 mb-4">
                  This grievance mechanism is established in compliance with:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Information Technology Act, 2000 (Section 79)</li>
                  <li>Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021</li>
                  <li>Consumer Protection Act, 2019</li>
                  <li>Other applicable Indian laws and regulations</li>
                </ul>
              </section>

              <section className="mb-8" id="complaint-form">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. File a Complaint</h2>
                <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-6 rounded-lg border">
                  <h3 className="text-xl font-medium text-gray-800 mb-4">Online Grievance Form</h3>
                  <p className="text-gray-700 mb-4">
                    Use the form below to submit your complaint. All fields marked with * are required.
                  </p>
                  
                  <form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          placeholder="+91-XXXXX-XXXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Complaint Type *
                        </label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500">
                          <option value="">Select complaint type</option>
                          <option value="inappropriate-content">Inappropriate Content</option>
                          <option value="fake-review">Fake Review/Rating</option>
                          <option value="defamatory-content">Defamatory Content</option>
                          <option value="copyright-violation">Copyright Violation</option>
                          <option value="privacy-violation">Privacy Violation</option>
                          <option value="harassment">Harassment/Bullying</option>
                          <option value="impersonation">Impersonation</option>
                          <option value="fraud">Fraudulent Activity</option>
                          <option value="technical-issue">Technical Issue</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Content URL/Location
                      </label>
                      <input
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        placeholder="https://ceremonzy.com/..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Detailed Description *
                      </label>
                      <textarea
                        rows="5"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        placeholder="Please provide a detailed description of your complaint..."
                      ></textarea>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Supporting Evidence
                      </label>
                      <input
                        type="file"
                        multiple
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        accept="image/*,.pdf,.doc,.docx"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Upload screenshots, documents, or other supporting evidence (Max 5MB per file)
                      </p>
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                      />
                      <label className="text-sm text-gray-700">
                        I declare that the information provided is true and accurate to the best of my knowledge. 
                        I understand that filing false complaints may result in action against my account.
                      </label>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full bg-pink-600 text-white py-3 px-4 rounded-lg hover:bg-pink-700 transition-colors font-medium"
                    >
                      Submit Complaint
                    </button>
                  </form>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GrievancePolicyPage;